import { createSlice } from "@reduxjs/toolkit";
import { InitialWorks, WorkModel } from "../domain/models/Work";
import { collectionStateCallback } from "../services/ApiServiceImpl";
import { saveWorkHandler, WorkServiceImpl } from "../services/WorkServiceImpl";
import { AppState } from "../store";
import { TFunction } from "i18next";
import { WorkDTO } from "../domain/models/dto/WorkDTO";
import { ApplicationServiceImpl } from "../services/ApplicationServiceImpl";
import { UserDTO } from "../domain/models/dto/UserDTO";
import { apiFilesURL, workConsideredNewTime } from "../Config";
import { selectUser } from "./user-slice";
import { WorkStatus } from "../domain/enums/WorkStatusEnum";
import _ from "lodash";
import { getCreatedTime } from "../tools/DatetimeTools";
import { ApplicationDTO } from "../domain/models/dto/ApplicationDTO";
import { ApplicationStatus } from "../domain/enums/ApplicationStatusEnum";
import { WorkType } from "../domain/enums/WorkTypeEnum";
import { differenceInHours, format } from "date-fns";
import { DEFAULT_PAGE } from "../domain/constants";
import { enUS, uk } from "date-fns/locale";
import languageServiceFactory from "../services/LanguageServiceImpl";
import { ReviewDTO } from "../domain/models/dto/ReviewDTO";
import { DistanceUnit } from "../domain/enums/common/DistanceUnitEnum";

const languageService = languageServiceFactory();

export const worksSlice = createSlice({
    name: "works",
    initialState: InitialWorks,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(
            WorkServiceImpl.getWorksThunk.fulfilled,
            (state, action) => {
                collectionStateCallback(state, action);
                state.isSavedWorksCollection =
                    action.meta.arg.getSavedWorks === true;
            }
        );

        // handle get /work/:id
        builder.addCase(
            WorkServiceImpl.getWorkByIdThunk.fulfilled,
            (state, action) => {
                state.isSavedWorksCollection = false;
                state.rows = [
                    ...state.rows.filter(
                        (item) => item.id !== action.payload.id
                    ),
                    action.payload,
                ];
            }
        );

        // handle post /work
        builder.addCase(
            WorkServiceImpl.createWorkThunk.fulfilled,
            (state, action) => {
                state.rows.push(action.payload);
            }
        );

        // handle post /application
        builder.addCase(
            ApplicationServiceImpl.createApplicationThunk.fulfilled,
            (state, action) => {
                const work = state.rows.find(
                    (item) => item.id === action.payload.workId
                );

                if (!work?.applications?.rows) {
                    return;
                }

                work.applications.rows.push(action.payload);
                work.applications.totalRows++;
            }
        );

        // handle patch /application/:id/approve
        builder.addCase(
            ApplicationServiceImpl.approveApplicationThunk.fulfilled,
            (state, action) => {
                const work = state.rows.find(
                    (work) => work.id === action.payload.workId
                );
                const application = work?.applications?.rows.find(
                    (app) => app.id === action.payload.id
                );
                if (application) {
                    const wasSelected = isApplicationAccepted(application);
                    application.status = action.payload.status;
                    if (
                        !wasSelected &&
                        isApplicationAccepted(action.payload) &&
                        Number.isInteger(work.selectedExecutorsCount)
                    ) {
                        work.selectedExecutorsCount++;
                    }
                }
                if (isWorkersHired(work)) {
                    work.status = WorkStatus.STAFFED;
                }
            }
        );

        // handle patch /application/:id/decline
        builder.addCase(
            ApplicationServiceImpl.declineApplicationThunk.fulfilled,
            (state, action) => {
                const work = state.rows.find(
                    (work) => work.id === action.payload.workId
                );
                const application = work?.applications?.rows.find(
                    (app) => app.id === action.payload.id
                );
                if (application) {
                    const wasSelected = isApplicationAccepted(application);
                    application.status = action.payload.status;
                    if (
                        wasSelected &&
                        !isApplicationAccepted(action.payload) &&
                        Number.isInteger(work.selectedExecutorsCount)
                    ) {
                        work.selectedExecutorsCount = Math.max(
                            0,
                            work.selectedExecutorsCount - 1
                        );
                    }
                }
                if (!isWorkersHired(work)) {
                    work.status = WorkStatus.ACTIVE;
                }
            }
        );

        // handle delete /work/:id
        builder.addCase(
            WorkServiceImpl.deleteWorkByIdThunk.fulfilled,
            (state, action) => {
                state.rows = state.rows.filter(
                    (item) => item.id !== action.payload.entityId
                );
            }
        );

        // handle patch /work/:id/complete
        builder.addCase(
            WorkServiceImpl.completeWorkThunk.fulfilled,
            (state, action) => {
                const work = state.rows.find(
                    (work) => work.id === action.payload.entityId
                );
                if (work) work.status = WorkStatus.DONE;
            }
        );

        // handle save /work/:id
        builder.addCase(
            WorkServiceImpl.saveWorkByIdThunk.pending,
            (state, action) => {
                if (!state.savingWorkIds.includes(action.meta.arg.id)) {
                    state.savingWorkIds.push(action.meta.arg.id);
                }
            }
        );
        builder.addCase(
            WorkServiceImpl.saveWorkByIdThunk.fulfilled,
            (state, action) => {
                state.savingWorkIds = state.savingWorkIds.filter(
                    (id) => id !== action.meta.arg.id
                );
                saveWorkHandler(state, action, state.isSavedWorksCollection);
            }
        );
        builder.addCase(
            WorkServiceImpl.saveWorkByIdThunk.rejected,
            (state, action) => {
                state.savingWorkIds = state.savingWorkIds.filter(
                    (id) => id !== action.meta.arg.id
                );
            }
        );

        // handle patch /application
        builder.addCase(
            ApplicationServiceImpl.updateApplicationThunk.fulfilled,
            (state, action) => {
                const work = state.rows.find(
                    (work) => work.id === action.payload.workId
                );
                work.applications.rows = work?.applications?.rows.filter(
                    (app) => app.id !== action.payload.id
                );
                work.applications?.rows.push(action.payload);
            }
        );

        // handle delete /application/:id
        builder.addCase(
            ApplicationServiceImpl.deleteApplicationThunk.fulfilled,
            (state, action) => {
                const work = state.rows.find(
                    (work) => work.id === action.payload.workId
                );

                if (!work?.applications?.rows) {
                    return;
                }

                work.applications.rows = work.applications.rows.filter(
                    (app) => app.id !== action.payload.entityId
                );
                work.applications.totalRows--;
            }
        );

        // handle get /application
        builder.addCase(
            ApplicationServiceImpl.getWorkApplicationsThunk.fulfilled,
            (state, action) => {
                const work = state.rows.find(
                    (work) => work.id === action.meta.arg.workId
                );
                if (
                    _.isArray(work.applications?.rows) &&
                    work.applications.rows?.length &&
                    action.meta.arg.params.page !== DEFAULT_PAGE
                ) {
                    work.applications.rows = work.applications.rows.concat(
                        action.payload.rows
                    );
                } else {
                    work.applications = action.payload;
                }
            }
        );
    },
});

export const selectWorks = (state: AppState) => state.works;

export const selectWorkById = (id: string) => {
    return (state: AppState) => {
        return state.works.rows.find((item) => item.id === id);
    };
};

export const selectIsWorkSavePending = (id: string) => {
    return (state: AppState) => state.works.savingWorkIds.includes(id);
};

export const getWorkCreatedTime = (
    t: TFunction,
    work: WorkDTO,
    useShortPrefix = false
) => {
    return getCreatedTime(
        t,
        work.createdAt,
        useShortPrefix ? "short" : "default"
    );
};

export const getApplicationCreatedTime = (
    t: TFunction,
    application: ApplicationDTO
) => {
    return getCreatedTime(t, application.createdAt);
};

export const getLocalizedApplicationCreatedTime = (
    application: ApplicationDTO
) => {
    if (!application?.createdAt) {
        return;
    }
    return format(new Date(application.createdAt), "d MMMM HH:mm", {
        locale: languageService.isUkrainian ? uk : enUS,
    });
};

export const getWorkUserProfileImage = (user: UserDTO, thumbnail = false) => {
    return user?.profileImage
        ? apiFilesURL +
              (thumbnail
                  ? user.profileImage.thumbnailPath
                  : user.profileImage.filePath)
        : "/images/no-photo.png";
};

export const isCurrentWorkOwner = (work: WorkDTO) => (state: AppState) => {
    return selectUser()(state).id === work?.clientId;
};

export const isWorkActive = (work: WorkDTO) =>
    _.get(work, "status") === WorkStatus.ACTIVE;
export const isWorkStaffed = (work: WorkDTO) =>
    _.get(work, "status") === WorkStatus.STAFFED;
export const isWorkDone = (work: WorkDTO) => work.status === WorkStatus.DONE;
export const isWorkDeleted = (work: WorkDTO) =>
    _.get(work, "status") === WorkStatus.DELETED;
export const isExpressWork = (work: WorkDTO) =>
    work?.type === WorkType.SHOW_CONTACTS_FOR_ALL && work?.expiresIn;

export const selectCurrentUserApplication =
    (work: WorkDTO) => (state: AppState) => {
        return work?.applications?.rows?.find(
            (application) =>
                _.get(application, "user.id") === selectUser()(state).id
        );
    };

export const isCurrentApplicationOwner =
    (work: WorkDTO, application: ApplicationDTO) => (state: AppState) => {
        return (
            application?.id === selectCurrentUserApplication(work)(state)?.id
        );
    };

export const selectWorkAvailableToEdit = (work: WorkDTO) => (state: AppState) =>
    (isWorkActive(work) || isWorkStaffed(work)) &&
    isCurrentWorkOwner(work)(state);

export const selectWorkAvailableToDelete =
    (work: WorkDTO) => (state: AppState) =>
        isWorkActive(work) &&
        getSelectedExecutorsCount(work) === 0 &&
        isCurrentWorkOwner(work)(state);

export const selectWorkAvailableToComplete =
    (work: WorkDTO) => (state: AppState) =>
        (isWorkActive(work) || isWorkStaffed(work)) &&
        getSelectedExecutorsCount(work) > 0 &&
        isCurrentWorkOwner(work)(state);

export const selectWorkAvailableToAddApplication =
    (work: WorkDTO) => (state: AppState) =>
        isWorkActive(work) &&
        !isExpressWork(work) &&
        !isCurrentWorkOwner(work)(state) &&
        !selectCurrentUserApplication(work)(state)?.id;

export const isApplicationResolved = (application: ApplicationDTO) =>
    application.status === ApplicationStatus.APPROVED ||
    application.status === ApplicationStatus.DECLINED;

export const isApplicationAccepted = (application: ApplicationDTO) =>
    application.status === ApplicationStatus.APPROVED;

export const isApplicationDeclined = (application: ApplicationDTO) =>
    application.status === ApplicationStatus.DECLINED;

export const getApprovedApplications = (work: WorkDTO) => {
    return (work?.applications?.rows || []).filter((item) =>
        isApplicationAccepted(item)
    );
};

/** Submitted applications are not executors until the server marks them APPROVED. */
export const getSelectedExecutorsCount = (work: WorkModel) =>
    Number.isInteger(work?.selectedExecutorsCount) &&
    work.selectedExecutorsCount >= 0
        ? work.selectedExecutorsCount
        : getApprovedApplications(work).length;

export const isWorkersHired = (work: WorkDTO) => {
    return getSelectedExecutorsCount(work) >= work.workersAmount;
};

export const isWorkConsideredNew = (work: WorkDTO) => {
    return (
        differenceInHours(new Date(), new Date(work.createdAt)) <=
        workConsideredNewTime
    );
};

export const findWorkReview = (subjectId: string, reviews: ReviewDTO[]) => {
    return reviews?.find((item) => item.subjectId === subjectId);
};

export const getWorkDistance = (work: WorkModel, t: TFunction) => {
    const distance = _.get(work, "distance");
    if (!distance) {
        return "";
    }
    switch (work.distanceUnit) {
        case DistanceUnit.METERS:
            return `distance ${t("common.metersShort")}`;
        case DistanceUnit.KILOMETERS:
            return `${_.round(distance, 1)} ${t("common.kilometersShort")}`;
        default:
            throw new Error("Distance unit not available");
    }
};

// export const {} = worksSlice.actions;

export default worksSlice.reducer;
